package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMBodyTextStyle
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMCardTitleTextStyle
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMLabelTextStyle
import ai.wagemix.web.theme.WMLeadTextStyle
import ai.wagemix.web.theme.WMPillCornerVar
import ai.wagemix.web.theme.WMStatTextStyle
import ai.wagemix.web.theme.WMTightGapVar
import ai.wagemix.web.theme.WMArticleWidthVar
import ai.wagemix.web.theme.WMCardStackGapVar
import ai.wagemix.web.theme.WMCardTextGapVar
import ai.wagemix.web.theme.WMClusterGapVar
import ai.wagemix.web.theme.WMControlHeightVar
import ai.wagemix.web.theme.WMHairGapVar
import ai.wagemix.web.theme.WMHeadingGapVar
import ai.wagemix.web.theme.WMOverlineTextStyle
import ai.wagemix.web.theme.WMPanelCornerVar
import ai.wagemix.web.theme.WMSectionTitleTextStyle
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import org.jetbrains.compose.web.css.FlexWrap
import androidx.compose.runtime.Composable
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.fontStyle
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

/**
 * Penpot landing section heading: an overline, the section title and an optional [lead], centred over the section
 * ("How it works", "Features", "Stories").
 */
@Composable
fun WMSectionHeading(overline: String, title: String, modifier: Modifier = Modifier, lead: String? = null) {
    val colors = ColorMode.current.toPalette().fs
    Column(
        modifier = Modifier.fillMaxWidth().gap(WMHeadingGapVar.value()).textAlign(TextAlign.Center).then(modifier),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(overline, WMOverlineTextStyle.toModifier().color(colors.primary))
        SpanText(title, WMSectionTitleTextStyle.toModifier())
        lead?.let { SpanText(it, WMLeadTextStyle.toModifier().color(colors.onSurfaceVariant).maxWidth(WMArticleWidthVar.value())) }
    }
}

/** Penpot `Wagemix / Feature Card`: an icon tile over a title and a short description. */
@Composable
fun WMFeatureCard(glyph: WMIconGlyph, title: String, description: String, modifier: Modifier = Modifier) {
    val colors = ColorMode.current.toPalette().fs
    WMCard(modifier = Modifier.height(100.percent).gap(WMCardStackGapVar.value()).then(modifier)) {
        WMIconTile(glyph)
        Column(modifier = Modifier.gap(WMCardTextGapVar.value())) {
            SpanText(title, WMCardTitleTextStyle.toModifier())
            SpanText(description, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
        }
    }
}

val WMStepNumberStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier
        .size(WMControlHeightVar.value())
        .borderRadius(WMPillCornerVar.value())
        .backgroundColor(colors.primary)
        .color(colors.onPrimary)
        .flexShrink(0)
}

/** Penpot `Wagemix / Step Card`: a numbered circle centred over a title and a description ("How it works"). */
@Composable
fun WMStepCard(number: Int, title: String, description: String, modifier: Modifier = Modifier) {
    val colors = ColorMode.current.toPalette().fs
    Column(
        modifier = Modifier.gap(WMCardStackGapVar.value()).textAlign(TextAlign.Center).then(modifier),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(modifier = WMStepNumberStyle.toModifier(), contentAlignment = Alignment.Center) {
            SpanText(number.toString(), WMLabelStrongTextStyle.toModifier())
        }
        SpanText(title, WMCardTitleTextStyle.toModifier())
        SpanText(description, WMBodyTextStyle.toModifier().color(colors.onSurfaceVariant))
    }
}

/** A story card shows at most this many occupation badges; the last one counts the rest ("Marketing Manager + 1"). */
private const val STORY_BADGE_LIMIT = 2

/** What a story card shows. */
data class WMStory(
    val quote: String,
    val occupations: List<String>,
    val initials: String,
    val name: String,
    val role: String
)

/**
 * Penpot `Wagemix / Story Card`: a quote, the mix as badges (at most two; the second counts the rest), a divider and
 * the person.
 */
@Composable
fun WMStoryCard(story: WMStory, modifier: Modifier = Modifier) {
    val colors = ColorMode.current.toPalette().fs
    WMCard(
        modifier = Modifier.height(100.percent).gap(WMCardStackGapVar.value()).borderRadius(WMPanelCornerVar.value()).then(modifier)
    ) {
        SpanText("“${story.quote}”", WMBodyTextStyle.toModifier().color(colors.onSurface))
        Row(modifier = Modifier.gap(WMInlineGapVar.value()).fillMaxWidth().flexWrap(FlexWrap.Wrap)) {
            val shown = story.occupations.take(STORY_BADGE_LIMIT)
            val hidden = story.occupations.size - shown.size
            shown.forEachIndexed { i, occupation ->
                val text = if (hidden > 0 && i == shown.lastIndex) "$occupation + $hidden" else occupation
                WMBadge(text, kind = WMBadgeKind.Primary)
            }
        }
        Box(modifier = Modifier.fillMaxWidth().height(WMHairlineVar.value()).backgroundColor(colors.outlineVariant))
        Row(modifier = Modifier.gap(WMClusterGapVar.value()), verticalAlignment = Alignment.CenterVertically) {
            WMAvatar(story.initials, accessibilityString = story.name)
            Column(modifier = Modifier.gap(WMHairGapVar.value())) {
                SpanText(story.name, WMLabelStrongTextStyle.toModifier())
                SpanText(story.role, WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant))
            }
        }
    }
}

/** One line of a plan card's feature list. */
data class WMPlanFeature(val text: String, val included: Boolean = true)

/** What a plan card shows; prices come from Stripe through the presenter. */
data class WMPlan(
    val name: String,
    /** "$0", "$49" or the placeholder while prices load. */
    val priceText: String,
    /** "Free forever", "Billed yearly" */
    val priceCaption: String,
    val description: String,
    val features: List<WMPlanFeature>,
    val highlighted: Boolean = false,
    val highlightLabel: String? = null,
    val current: Boolean = false
)

val WMPlanCardHighlightedStyle = CssStyle.base {
    Modifier.border(2.px, LineStyle.Solid, colorMode.toPalette().fs.primary)
}

/**
 * Penpot `Wagemix / Plan Card`: name, price, caption, description, a check-marked feature
 * list (crossed items dimmed) and an [action] slot ([Choose Basic]). The highlighted card
 * carries the "Most popular" badge and a primary border.
 */
@Composable
fun WMPlanCard(plan: WMPlan, modifier: Modifier = Modifier, action: @Composable () -> Unit) {
    val colors = ColorMode.current.toPalette().fs
    WMCard(
        modifier = Modifier.height(100.percent)
            .then(if (plan.highlighted) WMPlanCardHighlightedStyle.toModifier() else Modifier)
            .then(modifier)
    ) {
        Row(modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()), verticalAlignment = Alignment.CenterVertically) {
            SpanText(plan.name, WMCardTitleTextStyle.toModifier().fillMaxWidth())
            plan.highlightLabel?.let { WMBadge(it, kind = WMBadgeKind.Primary) }
        }
        Column(modifier = Modifier.gap(WMTightGapVar.value())) {
            SpanText(plan.priceText, WMStatTextStyle.toModifier())
            SpanText(plan.priceCaption, WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant))
        }
        SpanText(plan.description, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
        action()
        Column(modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()).role("list")) {
            plan.features.forEach { feature ->
                Row(modifier = Modifier.gap(WMInlineGapVar.value()).role("listitem"), verticalAlignment = Alignment.Top) {
                    WMIcon(
                        if (feature.included) WMIconGlyph.Check else WMIconGlyph.Minus,
                        accessibilityString = null,
                        modifier = Modifier.color(if (feature.included) colors.success else colors.onInactive).flexShrink(0)
                    )
                    SpanText(
                        feature.text,
                        WMBodySmallTextStyle.toModifier().color(if (feature.included) colors.onSurface else colors.onInactive)
                    )
                }
            }
        }
    }
}

/** A frequently-asked question and its answer. */
@Composable
fun WMFaqItem(question: String, answer: String, modifier: Modifier = Modifier) {
    val colors = ColorMode.current.toPalette().fs
    Column(
        modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value())
            .borderBottom(width = WMHairlineVar.value(), style = LineStyle.Solid, color = colors.outlineVariant)
            .then(modifier)
    ) {
        SpanText(question, WMLabelStrongTextStyle.toModifier())
        SpanText(answer, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
    }
}

/** A large figure with a label underneath (the landing trust strip: "800+ occupations"). */
@Composable
fun WMFigure(value: String, label: String, modifier: Modifier = Modifier) {
    val colors = ColorMode.current.toPalette().fs
    Column(modifier = Modifier.gap(WMTightGapVar.value()).then(modifier), horizontalAlignment = Alignment.CenterHorizontally) {
        SpanText(value, WMStatTextStyle.toModifier())
        SpanText(label, WMLabelTextStyle.toModifier().color(colors.onSurfaceVariant))
    }
}

/** The lead paragraph under a section title. */
@Composable
fun WMLead(text: String, modifier: Modifier = Modifier) {
    val colors = ColorMode.current.toPalette().fs
    SpanText(text, WMLeadTextStyle.toModifier().color(colors.onSurfaceVariant).then(modifier))
}
